import { useEffect, useRef, useState, type ReactNode } from 'react';

interface EmbedPdfViewerProps {
  src: string;
}

export function EmbedPdfViewer({ src }: Readonly<EmbedPdfViewerProps>): ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [useNativePreview, setUseNativePreview] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const initializeViewer = async (): Promise<void> => {
      setIsLoading(true);
      setUseNativePreview(false);
      container.replaceChildren();

      try {
        const { default: EmbedPDF } = await import('@embedpdf/snippet');

        if (isCancelled) {
          return;
        }

        EmbedPDF.init({
          type: 'container',
          target: container,
          src,
        });

        if (!isCancelled) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to initialize EmbedPDF viewer:', error);

        if (!isCancelled) {
          setUseNativePreview(true);
          setIsLoading(false);
        }
      }
    };

    void initializeViewer();

    return (): void => {
      isCancelled = true;
      container.replaceChildren();
    };
  }, [src]);

  return (
    <div className="relative h-full w-full bg-white">
      {useNativePreview ? (
        <object data={src} type="application/pdf" className="h-full w-full">
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-600">
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline underline-offset-2"
            >
              Open the PDF preview in a new tab.
            </a>
          </div>
        </object>
      ) : (
        <div ref={containerRef} className="h-full w-full" />
      )}
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-sm text-gray-500">
          Loading PDF preview...
        </div>
      ) : null}
      {useNativePreview ? (
        <div className="pointer-events-none absolute right-4 bottom-4 rounded bg-white/90 px-3 py-2 text-xs text-gray-600 shadow-sm">
          Using browser PDF preview fallback.
        </div>
      ) : null}
    </div>
  );
}
